import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Output,
  ViewChild,
} from '@angular/core';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { Router } from '@angular/router';
import { Lightbox } from 'ngx-lightbox';
import { SubProjectImmoText } from '../../../../model/project/sub-project-immo-text';
import { SubProjectControllerRequestService } from '../../../../services/api/sub-project-controller-request.service';
import { FilterStoreService } from '../../../../services/filter-store.service';
import { PageIndexService } from '../../../../services/page-index-service';
import { cloneDeep } from 'lodash';

import { AbstractImmoOverviewComponent } from '../abstract-immo-overview.component';
import { SnackService } from '../../../../services/snack.service';
import { BoardTypeChoices } from '../../../../model/product/board/board-part';
import { TourHelper } from 'src/app/helpers/tour-helper';
import { SignPersonalizedTextTypesChoices } from 'src/app/model/sign/enums/sign-personalized-text-type';
import { ClipboardWrapperService } from 'src/app/services/utility/clipboard/clipboard-wrapper.service';
import { StringHelper } from 'src/app/helpers/string-helper';
import { ImmoTextIpBoard } from 'src/app/model/project/immo-text-ip-board';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { TextImmoOverviewHelper } from './text-immo-overview-helper';
import DateHelper from 'src/app/helpers/date-helper';

//This component duplicates part of the html defined in the abstract component
//by having its own html file. I think this is preferred to the alternative that would make the original html
//way too complicated of a spaghetti file.
@Component({
  selector: 'text-immo-overview',
  templateUrl: './text-immo-overview.component.html',
  styleUrls: ['./text-immo-overview.component.scss'],
})
export class TextImmoOverviewComponent extends AbstractImmoOverviewComponent<
  SubProjectImmoText | GroupByItem | EmptyItem | DividerItem
> {
  @Output()
  countChange = new EventEmitter<number>();

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  constructor(
    protected filterStoreService: FilterStoreService,
    protected pageIndexService: PageIndexService,
    protected cd: ChangeDetectorRef,
    protected subProjectService: SubProjectControllerRequestService,
    protected _lightbox: Lightbox,
    protected router: Router,
    private snackService: SnackService,
    private clipboardWrapperService: ClipboardWrapperService
  ) {
    super(
      [
        {
          key: 'project__id',
          active: false,
          strictEquality: true,
          numeric: true,
        },
        {
          key: 'currentCreatedAt',
          active: false,
          strictEquality: true,
          numeric: false,
        },
        {
          key: 'immoNewCustomerName',
          active: false,
          strictEquality: false,
          numeric: false,
        },
      ],
      filterStoreService,
      pageIndexService,
      cd,
      subProjectService,
      _lightbox,
      router
    );
  }

  protected override getPaginator(): MatPaginator {
    return this.paginator;
  }

  protected override initDisplayedColumns(): void {
    this.displayedColumns = [
      'projectId',
      'placementDate',
      'installer',
      'branch',
      'customerName',
      'designInfo',
      'customText',
      'textAmount',
      'kind',
      'measurements',
      'textType',
      'design',
      'displayStatus',
      'markAsDoneAction',
    ];
  }

  public override openTour(
    subProject: SubProjectImmoText | GroupByItem | EmptyItem | DividerItem
  ): void {
    throw new Error('Method not implemented.');
  }
  protected override initSortSettings(): void {}

  protected override fetchItems(): void {
    this.loading = true;
    this.items = [];

    this.subProjectService
      .getImmoText(
        this.generateFilterString(),
        this.sortColumn,
        this.sortAscending,
        this.paginator.pageSize,
        this.paginator.pageIndex,
        false
      )
      .then((res) => {
        this.processPageResult(res);
        this.totalItems = res.totalItems;
        this.countChange.emit(this.totalItems);
        this.items = this.parseFetchedItems(res.items);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private parseFetchedItems(
    items: SubProjectImmoText[]
  ): (SubProjectImmoText | GroupByItem | EmptyItem | DividerItem)[] {
    const splitItems = this.splitFetchedItems(items);

    const black: SubProjectImmoText[] = [];
    const white: SubProjectImmoText[] = [];
    const red: SubProjectImmoText[] = [];
    const other: SubProjectImmoText[] = [];
    const board: SubProjectImmoText[] = [];

    for (const item of splitItems) {
      this.calculateStatusClass(item);
      if (
        (item.project.firstIpBoard.signTextType != null &&
          (item.project.firstIpBoard.signTextType.value ===
            SignPersonalizedTextTypesChoices.SEPARATE_SIGNS.value ||
            item.project.firstIpBoard.signTextType.value ===
              SignPersonalizedTextTypesChoices.SEPARATE_SIGNS_ABOVE.value)) ||
        item.project.firstIpBoard.splitForExtraBoard
      ) {
        board.push(item);
      } else {
        if (item.project.firstIpBoard.textColors == null) {
          //Andere teksten
          other.push(item);
        } else {
          const textColor = item.project.firstIpBoard.textColors
            .toLocaleLowerCase()
            .trim();
          if (textColor === 'zwart') {
            black.push(item);
          } else if (textColor === 'rood') {
            red.push(item);
          } else if (textColor === 'wit') {
            white.push(item);
          } else {
            other.push(item);
          }
        }
      }
    }

    if (board.length > 0) {
      TextImmoOverviewHelper.sortByTourDate(board);
    }

    const result: (
      | SubProjectImmoText
      | GroupByItem
      | EmptyItem
      | DividerItem
    )[] = [];

    result.push(new GroupByItem('Zwarte teksten', 'black-text-row'));
    if (black.length === 0) {
      result.push(new EmptyItem());
    } else {
      black.forEach((e) => result.push(e));
    }

    result.push(new GroupByItem('Rode teksten', 'red-text-row'));
    if (red.length === 0) {
      result.push(new EmptyItem());
    } else {
      red.forEach((e) => result.push(e));
    }

    result.push(new GroupByItem('Witte teksten', 'white-text-row'));
    if (white.length === 0) {
      result.push(new EmptyItem());
    } else {
      white.forEach((e) => result.push(e));
    }

    result.push(new GroupByItem('Andere teksten', 'other-text-row'));
    if (other.length === 0) {
      result.push(new EmptyItem());
    } else {
      other.forEach((e) => result.push(e));
    }

    result.push(new DividerItem());
    result.push(new GroupByItem('Tekstborden', 'text-board-row'));
    if (board.length === 0) {
      result.push(new EmptyItem());
    } else {
      board.forEach((e) => {
        if (
          !e.project.firstIpBoard.splitForExtraBoard &&
          e.project.firstIpBoard.backgroundColor
        ) {
          e.project.firstIpBoard.textTypeDisplay += ` + ${e.project.firstIpBoard.backgroundColor}`;
        }
        result.push(e);
      });
    }

    return result;
  }

  private calculateStatusClass(item: SubProjectImmoText): void {
    if (
      item.tourItem == null ||
      item.tourItem.tour == null ||
      item.tourItem.tour.atelierDate == null
    )
      return;

    this.calculateStatusClassForAtelierDate(
      item,
      item.tourItem.tour.atelierDate
    );
  }

  private calculateStatusClassForAtelierDate(
    item: SubProjectImmoText,
    atelierDate: Date
  ): void {
    const atelierDateMoment = DateHelper.parseMoment(atelierDate);

    if (atelierDateMoment.isSameOrBefore(this.today, 'day')) {
      item.statusClass = 'print-deadline-passed';
    } else if (
      atelierDateMoment.isSame(
        DateHelper.parseMoment(this.today).add(1, 'days'),
        'day'
      )
    ) {
      item.statusClass = 'print-deadline-soon';
    }
  }

  private splitFetchedItems(items: SubProjectImmoText[]): SubProjectImmoText[] {
    const result: SubProjectImmoText[] = [];

    for (const item of items) {
      if (item.tourItem != null) {
        item.currentConfirmedPlacementDateDisplay =
          TourHelper.getTourDateDisplay(item.tourItem);
      }

      if (item.project.ipBoards != null && item.project.ipBoards.length > 0) {
        item.project.ipBoards.forEach((ipBoard) => {
          if (!this.fullyRegistered(ipBoard)) {
            let splitItem: SubProjectImmoText = cloneDeep(item);

            splitItem.project.firstIpBoard = {
              ...ipBoard,
              ...ipBoard.boardPart,
            };
            delete splitItem.project.firstIpBoard['boardPart'];
            delete splitItem.project.firstIpBoard['otherSideBoardPart'];
            splitItem.project.firstIpBoard.textTypeDisplay = `${splitItem.project.firstIpBoard.textColors}`;
            splitItem.project.firstIpBoard.splitForText = true;

            if (
              splitItem.project.firstIpBoard.extraBoard != null &&
              !splitItem.project.firstIpBoard.extraBoardRegistered
            ) {
              this.splitForExtraBoard(splitItem, result);
            }
            if (splitItem.project.firstIpBoard.textRegistered) {
              return;
            }

            splitItem = this.calcSecondboardAmount(splitItem);

            if (
              splitItem.project.firstIpBoard.boardType != null &&
              (splitItem.project.firstIpBoard.boardType.ordinal ===
                BoardTypeChoices.V.ordinal ||
                splitItem.project.firstIpBoard.boardType.ordinal ===
                  BoardTypeChoices.DOUBLE_SIDED.ordinal)
            ) {
              if (
                !StringHelper.isEmpty(
                  splitItem.project.firstIpBoard.otherCustomText
                ) &&
                splitItem.project.firstIpBoard.otherCustomText !==
                  splitItem.project.firstIpBoard.customText
              ) {
                let otherSplitItem = cloneDeep(splitItem);
                otherSplitItem.project.firstIpBoard.customText =
                  splitItem.project.firstIpBoard.otherCustomText;
                result.push(otherSplitItem); //Split again
              } else if (
                ipBoard.otherSideBoardPart != null &&
                !StringHelper.isEmpty(ipBoard.otherSideBoardPart.customText)
              ) {
                let otherSplitItem = cloneDeep(item);
                otherSplitItem.project.firstIpBoard = {
                  ...ipBoard,
                  ...ipBoard.otherSideBoardPart,
                };
                delete otherSplitItem.project.firstIpBoard['boardPart'];
                delete otherSplitItem.project.firstIpBoard[
                  'otherSideBoardPart'
                ];
                otherSplitItem.project.firstIpBoard.textTypeDisplay = `${splitItem.project.firstIpBoard.textColors}`;
                otherSplitItem = this.calcSecondboardAmount(otherSplitItem);
                result.push(otherSplitItem); //Split again
              } else {
                const amount =
                  splitItem.project.firstIpBoard.amount != null
                    ? splitItem.project.firstIpBoard.amount
                    : 1;
                splitItem.project.firstIpBoard.amount = amount * 2;
              }
            }
            //no more field setting on splitItem after this point, because it's been split again!

            result.push(splitItem);
          }
        });
      }
    }

    return result;
  }

  private calcSecondboardAmount(
    splitItem: SubProjectImmoText
  ): SubProjectImmoText {
    if (splitItem.project.hasSecondBoard) {
      const amount =
        splitItem.project.firstIpBoard.amount != null
          ? splitItem.project.firstIpBoard.amount
          : 1;
      splitItem.project.firstIpBoard.amount = amount * 2;
    }
    return splitItem;
  }

  private fullyRegistered(ipBoard: ImmoTextIpBoard): boolean {
    return (
      ipBoard.textRegistered &&
      (ipBoard.extraBoard == null || ipBoard.extraBoardRegistered)
    );
  }

  private splitForExtraBoard(
    splitItem: SubProjectImmoText,
    accumulator: SubProjectImmoText[]
  ): void {
    if (splitItem.project.firstIpBoard.extraBoard != null) {
      const extraBoardItem: SubProjectImmoText = cloneDeep(splitItem);
      extraBoardItem.project.firstIpBoard.customText = `Extra ${extraBoardItem.project.firstIpBoard.extraBoard.label.toLocaleLowerCase()}: ${
        extraBoardItem.project.firstIpBoard.extraBoardText
      }`;
      extraBoardItem.project.firstIpBoard.textTypeDisplay =
        extraBoardItem.project.firstIpBoard.extraBoardColor;
      extraBoardItem.project.firstIpBoard.splitForText = false;
      extraBoardItem.project.firstIpBoard.splitForExtraBoard = true;

      if (
        extraBoardItem.project.firstIpBoard.boardType != null &&
        (extraBoardItem.project.firstIpBoard.boardType.ordinal ===
          BoardTypeChoices.V.ordinal ||
          extraBoardItem.project.firstIpBoard.boardType.ordinal ===
            BoardTypeChoices.DOUBLE_SIDED.ordinal)
      ) {
        extraBoardItem.project.firstIpBoard.amount = 2;
      } else {
        extraBoardItem.project.firstIpBoard.amount = null;
      }

      accumulator.push(extraBoardItem);
    }
  }

  public openProject(subProject: SubProjectImmoText) {
    this.router.navigate([
      '/project',
      subProject.project == null ? null : subProject.project.id,
      subProject.id,
    ]);
  }

  public copyProjectId(subProject: SubProjectImmoText): void {
    let toCopyText = '';
    if (subProject.project != null && subProject.project.id != null) {
      toCopyText = subProject.project.id.toString();
    }
    if (subProject.tourItem != null && subProject.tourItem.tour != null) {
      let placerName = null;
      let dateString = null;
      if (
        subProject.tourItem.tour.placer != null &&
        subProject.tourItem.tour.placer.user != null
      ) {
        placerName = StringHelper.join(
          [
            subProject.tourItem.tour.placer.user.firstName == null
              ? null
              : subProject.tourItem.tour.placer.user.firstName.toString()[0],
            subProject.tourItem.tour.placer.user.lastName == null
              ? null
              : subProject.tourItem.tour.placer.user.lastName.toString()[0],
          ],
          ''
        );
      }
      if (subProject.tourItem.tour.date != null) {
        dateString = DateHelper.formatDate(subProject.tourItem.tour.date);
      }
      toCopyText = StringHelper.join([toCopyText, placerName, dateString], ' ');
    }
    if (!StringHelper.isEmptyOrWhiteSpace(toCopyText)) {
      this.clipboardWrapperService.copyToClipboard(toCopyText);
    }
  }

  public copyToClipboard(text: string): void {
    if (!StringHelper.isEmptyOrWhiteSpace(text)) {
      const toCopyText = text.startsWith('Extra bovenbord: ')
        ? text.replace('Extra bovenbord: ', '')
        : text.startsWith('Extra onderbord: ')
        ? text.replace('Extra onderbord: ', '')
        : text;
      this.clipboardWrapperService.copyToClipboard(toCopyText);
    }
  }

  protected getLocalStorageKey(): string {
    return 'TextImmoOverviewComponent';
  }

  save(): void {
    if (!CollectionHelper.isPopulated(this.items)) {
      return;
    }

    const ipBoardIdsRegisterText = [];
    const ipBoardIdsRegisterExtraBoard = [];

    this.items.forEach((i) => {
      if (this.isItem(null, i) && i.project.firstIpBoard.markedAsDone) {
        if (i.project.firstIpBoard.splitForText) {
          ipBoardIdsRegisterText.push(i.project.firstIpBoard.id);
        } else if (i.project.firstIpBoard.splitForExtraBoard) {
          ipBoardIdsRegisterExtraBoard.push(i.project.firstIpBoard.id);
        }
      }
    });

    if (
      ipBoardIdsRegisterText.length === 0 &&
      ipBoardIdsRegisterExtraBoard.length === 0
    ) {
      return;
    }

    this.subProjectService
      .immoTextRegister(ipBoardIdsRegisterText, ipBoardIdsRegisterExtraBoard)
      .then(() => {
        this.snackService.showSuccess('Registraties succesvol opgeslagen');
        this.fetchItems();
      });
  }

  isGroup(
    index: number,
    item: GroupByItem | SubProjectImmoText | EmptyItem | DividerItem
  ): boolean {
    if (item instanceof GroupByItem) {
      return true;
    } else {
      return false;
    }
  }

  isItem(index: number, item: any): item is SubProjectImmoText {
    return item.id != null;
  }

  isEmpty(
    index: number,
    item: GroupByItem | SubProjectImmoText | EmptyItem | DividerItem
  ): boolean {
    if (item instanceof EmptyItem) {
      return true;
    } else {
      return false;
    }
  }

  isDivider(
    index: number,
    item: GroupByItem | SubProjectImmoText | EmptyItem | DividerItem
  ): boolean {
    if (item instanceof DividerItem) {
      return true;
    } else {
      return false;
    }
  }
}

class GroupByItem {
  header: string;
  rowClass: string;

  constructor(header: string, rowClass: string) {
    this.header = header;
    this.rowClass = rowClass;
  }
}

class EmptyItem {}

class DividerItem {}
